/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { Button, DatePicker, Form, Input, Select, Space, Typography } from 'antd';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import commonStyles from '../../../../../styles/commonStyles';
import { datePickerFormat } from '../../../../../i18n';
import type { Field } from '../../../../../models/Field';
import type { Plot } from '../../../../../models/Plot';
import type { Bed } from '../../../../../models/Bed';
import { normalizeSelectOptions } from '../utils';
import { formStyles } from '../constants';
import type { PlantingFormModalValues } from '../types';

interface LocationsListSectionProps {
  form: FormInstance<PlantingFormModalValues>;
  fields: Field[];
  fieldsLoading: boolean;
  plotsByField: Record<string, Plot[]>;
  bedsByPlot: Record<string, Bed[]>;
  plotsLoading: boolean;
  bedsLoading: boolean;
  isEditMode: boolean;
  allowAddLocations: boolean;
  locationsListRules: any[];
  onFieldChange: (formIndex: number, fieldId: number | string | undefined) => void;
  onPlotChange: (formIndex: number, plotId: number | string | undefined) => void;
  onBedChange: (bedId: string | undefined) => void;
}

/** Dynamic Form.List for adding/removing planting locations with cascade selects */
const LocationsListSection: React.FC<LocationsListSectionProps> = ({
  form,
  fields,
  fieldsLoading,
  plotsByField,
  bedsByPlot,
  plotsLoading,
  bedsLoading,
  isEditMode,
  allowAddLocations,
  locationsListRules,
  onFieldChange,
  onPlotChange,
  onBedChange,
}) => {
  const { t } = useTranslation('plantings');
  const fieldOptions = useMemo(() => normalizeSelectOptions<Field>(fields), [fields]);

  return (
    <>
      <Typography.Title level={5} css={formStyles.sectionTitle}>
        {t('form.location.listTitle')}
      </Typography.Title>

      <Form.List name="locations" rules={locationsListRules}>
        {(fieldsList, { add, remove }, { errors }) => (
          <>
            {fieldsList.map((fieldItem, index) => {
              const fieldId = form.getFieldValue(['locations', fieldItem.name, 'fieldId']);
              const plotId = form.getFieldValue(['locations', fieldItem.name, 'plotId']);
              const plotOptions = normalizeSelectOptions<Plot>(plotsByField[String(fieldId)] || []);
              const bedOptions = normalizeSelectOptions<Bed>(bedsByPlot[String(plotId)] || []);

              return (
                <Space
                  key={fieldItem.key}
                  direction="vertical"
                  css={formStyles.locationContainer}
                >
                  <Space css={formStyles.locationHeader} align="start">
                    <Typography.Text strong>{t('form.location.numbered', { index: index + 1 })}</Typography.Text>
                    {fieldsList.length > 1 && allowAddLocations && (
                      <Button type="link" danger onClick={() => remove(fieldItem.name)}>
                        {t('common:actions.delete')}
                      </Button>
                    )}
                  </Space>

                  <Form.Item name={[fieldItem.name, 'id']} hidden>
                    <Input type="hidden" />
                  </Form.Item>

                  <Form.Item
                    label={t('details.field')}
                    name={[fieldItem.name, 'fieldId']}
                    rules={[{ required: true, message: t('form.location.fieldRequired') }]}
                  >
                    <Select
                      placeholder={t('form.location.fieldRequired')}
                      loading={fieldsLoading || plotsLoading}
                      options={fieldOptions}
                      onChange={(value) => onFieldChange(fieldItem.name, value)}
                      allowClear={allowAddLocations}
                      disabled={isEditMode}
                    />
                  </Form.Item>

                  <Form.Item
                    label={t('details.plot')}
                    name={[fieldItem.name, 'plotId']}
                    rules={[{ required: true, message: t('form.location.plotRequired') }]}
                  >
                    <Select
                      placeholder={fieldId ? t('form.location.plotPlaceholder') : t('form.location.fieldFirst')}
                      disabled={isEditMode || !fieldId}
                      loading={plotsLoading}
                      options={plotOptions}
                      onChange={(value) => onPlotChange(fieldItem.name, value)}
                      allowClear={allowAddLocations}
                    />
                  </Form.Item>

                  <Form.Item
                    label={t('details.bed')}
                    name={[fieldItem.name, 'bedId']}
                    rules={[{ required: true, message: t('form.location.bedRequired') }]}
                  >
                    <Select
                      placeholder={plotId ? t('form.location.bedRequired') : t('form.location.plotFirst')}
                      disabled={isEditMode || !plotId}
                      loading={bedsLoading}
                      options={bedOptions}
                      showSearch
                      optionFilterProp="label"
                      onChange={(bedId) => onBedChange(bedId ? String(bedId) : undefined)}
                    />
                  </Form.Item>

                  <Space direction="horizontal" css={formStyles.fullWidthSpace}>
                    <Form.Item
                      label={t('details.startDate')}
                      name={[fieldItem.name, 'fromDate']}
                      rules={[{ required: true, message: t('form.location.startDateRequired') }]}
                      css={formStyles.flexOne}
                    >
                      <DatePicker format={datePickerFormat()} css={commonStyles.fullWidth} />
                    </Form.Item>
                    <Form.Item
                      label={t('form.location.endDate')}
                      name={[fieldItem.name, 'toDate']}
                      css={formStyles.flexOne}
                    >
                      <DatePicker format={datePickerFormat()} css={commonStyles.fullWidth} allowClear />
                    </Form.Item>
                  </Space>
                </Space>
              );
            })}

            <Form.ErrorList errors={errors} />

            {allowAddLocations && (
              <Button type="dashed" onClick={() => add()} block>
                {t('form.location.add')}
              </Button>
            )}
          </>
        )}
      </Form.List>
    </>
  );
};

export default LocationsListSection;
